import { Stripe, loadStripe } from "@stripe/stripe-js";
import { classes } from "../../../scripts/constants";
import { postJson, queryFirstDocument, trackEvent } from "../../../utils";
import {
  ApiResponse,
  ApiResponseWithData,
  BasketResponse,
  GetBasketResponse,
  SetDeliveryDetailsRequest,
  SetDeliveryMethodRequest,
} from "../../ticketing/basket-v2/api/models";
import { logError, reportError } from "../../ticketing/basket-v2/errorReporting";
import { constants } from "../../ticketing/common";
import { endLoadingState, loadingTemplate, startLoadingState } from "../basket-v2/basketLoading";

let config = {
  selectors: {
    quickpayButtonSelector: "#payment-request-button",
    termsContainerSelector: ".basket-pre-checkout--tandc",
    paymentContainerSelector: ".basket-pre-checkout--payment",
    acceptTermsButtonSelector: "[data-accept-terms]",
    beginCheckoutButtonSelector: "[data-begin-checkout]",
  },
  redirectUri: "",
};

let paymentTermsContainer: HTMLElement = null;
let paymentPaymentContainer: HTMLElement = null;
let quickPayButton: HTMLElement = null;
let acceptTermsButton: HTMLElement = null;
let beginCheckoutButton: HTMLElement = null;
let stripe: Stripe;
const loadingBasketMessage = loadingTemplate("Updating basket...");

export async function initStripeRequestPaymentButton() {
  paymentTermsContainer = queryFirstDocument<HTMLElement>(config.selectors.termsContainerSelector);
  paymentPaymentContainer = queryFirstDocument<HTMLElement>(config.selectors.paymentContainerSelector);
  quickPayButton = queryFirstDocument<HTMLElement>(config.selectors.quickpayButtonSelector);
  acceptTermsButton = queryFirstDocument<HTMLElement>(config.selectors.acceptTermsButtonSelector);
  beginCheckoutButton = queryFirstDocument<HTMLElement>(config.selectors.beginCheckoutButtonSelector);

  if (quickPayButton !== undefined) {
    await initialize();
    bindEvents();
  } else if (paymentTermsContainer) {
    paymentTermsContainer.classList.remove(classes.LOADING);
  }
}

async function initialize() {
  if (!("enableQuickpay" in paymentTermsContainer.dataset)) {
    paymentTermsContainer.classList.remove(classes.LOADING);
    return;
  }

  try {
    const response = await fetch(constants.urls.payment.create, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    
    if (!response.ok) {
      logError(response.status, response.statusText, "There was an error initializing quick payment");
      paymentTermsContainer.classList.remove(classes.LOADING);
      disableQuickPay();
      return;
    }

    var responseJson: any = await response.json();

    if (responseJson.amount > 0.0) {
      stripe = await loadStripe(responseJson.apiKey, {
        stripeAccount: responseJson.stripeAccount,
      });

      const paymentRequest = stripe.paymentRequest({
        country: responseJson.country,
        currency: responseJson.currency.toLowerCase(),
        total: {
          label: responseJson.description,
          amount: ~~Number(responseJson.amount * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestShipping: true,
        requestPayerPhone: true,
        shippingOptions: responseJson.shippingOptions,
      });

      const elements = stripe.elements();
      const prButton = elements.create("paymentRequestButton", {
        paymentRequest,

        style: {
          paymentRequestButton: {
            theme: "light",
          },
        },
      });

      const clientSecret = responseJson.clientSecret;
      const uri = new URL(responseJson.redirectUri);
      uri.searchParams.append("payment_intent_client_secret", clientSecret);
      uri.searchParams.append("order_number", responseJson.orderId);
      config.redirectUri = uri.href;

      // Check the availability of the Payment Request API first.
      const result = await paymentRequest.canMakePayment();
      if (result) {
        prButton.mount(config.selectors.quickpayButtonSelector);
      } else {
        disableQuickPay();
      }

      paymentTermsContainer.classList.remove(classes.LOADING);

      paymentRequest.on("token", async () => {
        startLoadingState(loadingBasketMessage, ".basket-overlay");
      });

      paymentRequest.on("shippingaddresschange", async (ev) => {
        if (responseJson.allowedCountries.includes(ev.shippingAddress.country)) {
          ev.updateWith({
            status: "success",
            shippingOptions: responseJson.shippingOptions,
          });
        } else {
          ev.updateWith({ status: "invalid_shipping_address" });
        }
      });

      paymentRequest.on("shippingoptionchange", async (ev) => {
        const request: SetDeliveryMethodRequest = {
          checkoutPageId: 0,
          deliveryMethod: ev.shippingOption.id,
        };

        let setDeliveryMethodResponse: ApiResponseWithData<GetBasketResponse>;

        try {
          setDeliveryMethodResponse = await postJson<ApiResponseWithData<GetBasketResponse>>(
            constants.urls.basketV2.delivery_method,
            request
          );
        } catch (err) {
          ev.updateWith({ status: "fail" });
          return;
        }

        if (!setDeliveryMethodResponse || !setDeliveryMethodResponse.success) {
          ev.updateWith({ status: "fail" });
          return;
        }

        ev.updateWith({
          status: "success",
          total: {
            amount: Math.round(parseFloat(setDeliveryMethodResponse.data.total) * 100),
            label: ev.shippingOption.label,
          },
        });
      });

      paymentRequest.on("paymentmethod", async (ev) => {
        //Set shipping method
        const request: SetDeliveryMethodRequest = {
          checkoutPageId: 0,
          deliveryMethod: ev.shippingOption.id,
        };

        let setDeliveryMethodResponse: ApiResponse;

        try {
          setDeliveryMethodResponse = await postJson<ApiResponse>(constants.urls.basketV2.delivery_method, request);
        } catch (err) {
          handleError(ev);
          return;
        }

        if (!setDeliveryMethodResponse || !setDeliveryMethodResponse.success) {
          handleError(ev);
          return;
        }

        //Set shipping address
        var fullname = ev.payerName;
        var detailsRequest: SetDeliveryDetailsRequest = {
          firstName: fullname.substring(0, fullname.indexOf(" ")),
          lastName: fullname.substring(fullname.indexOf(" ") + 1),
          email: ev.payerEmail,
          phoneNumber: ev.payerPhone,
          addressLine1: ev.shippingAddress.addressLine[0],
          addressLine2: ev.shippingAddress.addressLine.length > 1 ? ev.shippingAddress.addressLine[1] : null,
          townCity: ev.shippingAddress.city,
          postalCode: ev.shippingAddress.postalCode,
          country: ev.shippingAddress.country,
        };

        const apiUrl = constants.urls.basketV2.delivery_details;

        let setDeliveryDetailsResponse: ApiResponseWithData<BasketResponse>;

        try {
          setDeliveryDetailsResponse = await postJson<ApiResponseWithData<BasketResponse>>(apiUrl, detailsRequest);
        } catch (err) {
          handleError(ev);
          return;
        }

        if (!setDeliveryDetailsResponse || !setDeliveryDetailsResponse.success) {
          handleError(ev);
          return;
        }

        // Confirm the PaymentIntent
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: ev.paymentMethod.id,
            //receipt_email: ev.payerEmail,
            shipping: {
              name: ev.payerName,
              phone: ev.payerPhone,
              address: {
                line1: ev.shippingAddress.addressLine[0],
                postal_code: ev.shippingAddress.postalCode,
                city: ev.shippingAddress.city,
                country: ev.shippingAddress.country,
              },
            },
          },
          { handleActions: false }
        );

        if (confirmError) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          ev.complete("fail");
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete("success");
          // Check if the PaymentIntent requires any actions and if so let Stripe.js
          // handle the flow. If using an API version older than "2019-02-11"
          // instead check for: `paymentIntent.status === "requires_source_action"`.
          if (paymentIntent.status === "requires_action") {
            // Let Stripe.js handle the rest of the payment flow.
            const { error } = await stripe.confirmCardPayment(clientSecret);
            if (error) {
              // The payment failed -- ask your customer for a new payment method.
              reportError(0, error.message);
            } else {
              trackEvent({
                event: "basket",
                category: "Basket",
                action: "Quick Pay",
                label: ev.walletName,
              });

              // The payment has succeeded.
              window.location.assign(config.redirectUri);
            }
          } else {
            // The payment has succeeded.
            window.location.assign(config.redirectUri);
          }
        }
      });
    }
  } catch (err) {
    logError(500, err, "Error initializing or processing quick payment button");
  }
}

function handleError(ev) {
  endLoadingState(".basket-overlay");
  ev.complete("fail");
}

function bindEvents() {
  acceptTermsButton.addEventListener("click", (e) => {
    e.preventDefault();
    paymentTermsContainer.classList.add(classes.HIDDEN);
    paymentPaymentContainer.classList.remove(classes.HIDDEN);
  });
}

function disableQuickPay() {
  acceptTermsButton.classList.add(classes.HIDDEN);
  beginCheckoutButton.classList.remove(classes.HIDDEN);
}
